import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { StatusPill } from "@/components/app/Primitives";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

/** Invite Influencer — verify + optionally correct contact before sending the invite. */
export function InviteInfluencerDialog({ order, open, onOpenChange, onDone, testIdPrefix }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inf, setInf] = useState(null);

  useEffect(() => {
    if (!order || !open) return;
    setF({
      legal_name: order.influencer_name || "",
      email: order.influencer_email || "",
      mobile: order.influencer_mobile || "",
      country: "India",
      influencer_type: "individual",
      company_name: "",
      note: "",
    });
    if (order.influencer_id) {
      api.get(`/influencers/${order.influencer_id}`).then((r) => {
        setInf(r.data);
        setF((cur) => ({
          ...cur,
          legal_name: r.data.legal_name || cur.legal_name,
          email: r.data.email || cur.email,
          mobile: r.data.mobile || cur.mobile,
          country: r.data.country || cur.country,
          influencer_type: r.data.influencer_type || cur.influencer_type,
          company_name: r.data.company_name || "",
        }));
      }).catch((err) => {
        console.debug("influencer prefill failed:", err?.message);
      });
    }
  }, [order, open]);

  if (!f) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/invite-influencer`, f);
      toast.success("Invite sent — influencer will receive OTP registration link");
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={`${testIdPrefix}-invite-dialog`}>
        <DialogHeader>
          <DialogTitle>Verify & invite influencer</DialogTitle>
          <DialogDescription>
            Please confirm the influencer contact details supplied by <b>{order.tenant_name}</b>. You may correct
            typos before sending the OTP-based invitation.
          </DialogDescription>
        </DialogHeader>

        <Alert className="mb-2">
          <AlertTitle className="text-xs font-bold uppercase tracking-widest">From order {order.order_number}</AlertTitle>
          <AlertDescription className="text-xs">
            Campaign: <b>{order.campaign}</b> · Value: <b>{order.currency} {Number(order.order_value).toLocaleString()}</b>
          </AlertDescription>
        </Alert>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Legal name *</label>
            <Input value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Email *</label>
              <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Mobile *</label>
              <Input value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Country</label>
              <Input value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Type</label>
              <select value={f.influencer_type} onChange={(e) => setF({ ...f, influencer_type: e.target.value })} className="h-10 w-full rounded-md border px-3 text-sm">
                <option value="individual">Individual</option>
                <option value="business">Business / LLP</option>
              </select>
            </div>
            {f.influencer_type === "business" && (
              <div className="col-span-2">
                <label className="text-xs font-semibold text-slate-600">Company / Trade name</label>
                <Input value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} />
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Note to influencer (optional)</label>
            <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Anything the influencer should know before onboarding…" />
          </div>
          {inf && inf.compliance_status && inf.compliance_status !== "invited" && (
            <div className="text-xs text-slate-600 border rounded p-2 bg-slate-50">
              Current profile status: <StatusPill status={inf.compliance_status} />
            </div>
          )}
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid={`${testIdPrefix}-invite-submit`} onClick={submit} disabled={busy || !f.legal_name || !f.email || !f.mobile} className="bg-[#0052FF] hover:bg-[#0043D9]">
            {busy ? "Sending…" : "Send invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Issue back-to-back PO — copy customer PO lines, exclude ProPay fee + Early Payment Fee. */
export function IssuePODialog({ order, open, onOpenChange, onDone, testIdPrefix }) {
  const [lines, setLines] = useState([]);
  const [terms, setTerms] = useState(15);
  const [note, setNote] = useState("");
  const [inf, setInf] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!order || !open) return;
    setLines((order.services || []).map((s) => ({ ...s, count: Number(s.count || 1), fee: Number(s.fee || 0) })));
    setTerms(order.influencer_payment_terms_days || 15);
    setNote("");
    if (order.influencer_id) {
      api.get(`/influencers/${order.influencer_id}`).then((r) => setInf(r.data)).catch(() => {});
    }
  }, [order, open]);

  const total = lines.reduce((sum, l) => sum + (parseFloat(l.fee) || 0), 0);
  const canSubmit = lines.length > 0 && total > 0 && lines.every((l) => l.type);
  const kysOk = inf?.compliance_status === "verified";

  const update = (i, k, v) => setLines(lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const add = () => setLines([
    ...lines,
    {
      _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `l_${Math.random().toString(36).slice(2, 10)}`,
      type: "", count: 1, description: "", fee: 0,
    },
  ]);
  const remove = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const submit = async () => {
    setBusy(true);
    try {
      // Strip the client-only stable-key before submission.
      const cleaned = lines.map(({ _key, ...rest }) => rest);
      const { data } = await api.post(`/orders/${order.id}/issue-po`, {
        services: cleaned, payment_terms_days: terms, include_note: note,
      });
      toast.success(`PO ${data.purchase_order.po_number} issued`);
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to issue PO");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid={`${testIdPrefix}-issuepo-dialog`}>
        <DialogHeader>
          <DialogTitle>Issue back-to-back Purchase Order</DialogTitle>
          <DialogDescription>
            Copy the confirmed lines from the FMCG order into the influencer PO. <b>ProPay fee</b> and <b>Early Payment Fee</b> are
            never included on the influencer PO — they remain confidential between FMCG and ProPay.
          </DialogDescription>
        </DialogHeader>

        {!kysOk && (
          <Alert variant="destructive" className="mb-2">
            <AlertTitle>KYS not complete</AlertTitle>
            <AlertDescription>
              This influencer is not KYS-verified yet (status: <b>{inf?.compliance_status || "loading…"}</b>).
              Ask them to finish onboarding before you issue the PO.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 rounded-lg p-3 mb-3">
          <div><div className="text-xs text-slate-500 uppercase tracking-widest">Issued by</div><div className="font-semibold">PROPAY GLOBAL PRIVATE LIMITED</div></div>
          <div><div className="text-xs text-slate-500 uppercase tracking-widest">Issued to</div><div className="font-semibold">{order.influencer_name}</div></div>
          <div><div className="text-xs text-slate-500 uppercase tracking-widest">Campaign</div><div>{order.campaign}</div></div>
          <div><div className="text-xs text-slate-500 uppercase tracking-widest">Customer PO ref</div><div className="font-mono">{order.fmcg_po_number}</div></div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 bg-slate-100 px-2 py-2 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            <div className="col-span-4">Line item</div>
            <div className="col-span-1 text-center">Qty</div>
            <div className="col-span-4">Description</div>
            <div className="col-span-2 text-right">Fee</div>
            <div className="col-span-1"></div>
          </div>
          {lines.map((l, i) => (
            <div key={l._key || i} className="grid grid-cols-12 gap-2 items-center px-2 py-2 border-t">
              <Input className="col-span-4" placeholder="Instagram Reel" value={l.type} onChange={(e) => update(i, "type", e.target.value)} />
              <Input className="col-span-1" type="number" value={l.count} onChange={(e) => update(i, "count", parseInt(e.target.value) || 1)} />
              <Input className="col-span-4" value={l.description || ""} onChange={(e) => update(i, "description", e.target.value)} />
              <Input className="col-span-2" type="number" value={l.fee} onChange={(e) => update(i, "fee", parseFloat(e.target.value) || 0)} />
              <button type="button" onClick={() => remove(i)} className="col-span-1 text-rose-600 text-sm">✕</button>
            </div>
          ))}
          <div className="px-2 py-2 border-t bg-slate-50 flex justify-between">
            <button type="button" onClick={add} className="text-sm text-[#0052FF] font-semibold">+ Add line</button>
            <div className="text-sm font-semibold">Influencer PO total: {order.currency} {total.toLocaleString()}</div>
          </div>
        </div>

        {/* Fee split banner — Procurement Desk sees the FMCG side vs the
            Influencer side of the transaction, so it's crystal clear that
            ProPay's margin + any Early Payment Fee stay OUT of the
            influencer PO. */}
        <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3 mt-3 text-sm space-y-1">
          <div className="text-xs font-bold uppercase tracking-widest text-emerald-800 mb-1">Fee split — Confidential (Procurement view)</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1">
            <div className="text-slate-700">FMCG PO value ({order.tenant_name} → ProPay)</div>
            <div className="font-mono text-right font-semibold text-slate-900" data-testid="issuepo-fmcg-total">{order.currency} {Number(order.order_value || 0).toLocaleString()}</div>
            <div className="text-slate-700">− Influencer PO (ProPay → {order.influencer_name})</div>
            <div className="font-mono text-right text-slate-900" data-testid="issuepo-influencer-total">− {order.currency} {total.toLocaleString()}</div>
            <div className="border-t border-emerald-300 pt-1 text-emerald-900 font-bold">= ProPay retained (fees + margin)</div>
            <div className="border-t border-emerald-300 pt-1 font-mono text-right text-emerald-900 font-bold" data-testid="issuepo-propay-retained">
              {order.currency} {Math.max(Number(order.order_value || 0) - total, 0).toLocaleString()}
            </div>
          </div>
          <div className="text-[10px] text-emerald-800 italic pt-1">The influencer only sees the {order.currency} {total.toLocaleString()} figure on their PO — the retained amount is invisible to them.</div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Influencer payment terms (days)</label>
            <Input type="number" value={terms} onChange={(e) => setTerms(parseInt(e.target.value) || 0)} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Note to influencer (optional)</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Kick-off call scheduled Friday" />
          </div>
        </div>

        <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-2 mt-3">
          Confirm carefully — once submitted, the PO is issued from PROPAY GLOBAL PRIVATE LIMITED to the influencer and cannot be edited.
          The ProPay fee (charged separately to {order.tenant_name}) and any Early Payment Fee are captured in the internal ledger only.
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid={`${testIdPrefix}-issuepo-submit`} onClick={submit} disabled={busy || !canSubmit || !kysOk} className="bg-[#0052FF] hover:bg-[#0043D9]">
            {busy ? "Issuing…" : "Verify & Issue PO"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
